import { useParams, useMatch, useSearch } from '@tanstack/react-router'
import type { Profile } from '@/lib/profile-search-schema'

export type ProfileRouteParams = {
  jobId?: string
  offerId?: string
  employeeId?: string
  teamId?: string
  groupId?: string
  applicantId?: string
}

// Shared with the route loaders so a prefetched getReport key matches the component's, avoiding a double fetch.
export function deriveProfiles(
  search: { first?: Profile; second?: Profile },
  params: ProfileRouteParams,
  isReportPage: boolean,
  precedence: 'search' | 'params' = 'search',
  isMarketPage = false,
) {
  const { jobId, offerId, employeeId, teamId, groupId, applicantId } = params

  const first =
    (precedence === 'search' ? search.first : undefined) ??
    (isReportPage
      ? ({ type: 'applicant' } as const)
      : isMarketPage
        ? // The market page defaults to the market-wide profile so a bare `/market` URL loads a report
          // instead of an empty selection state (the breadcrumb sets the same `{ type: 'market' }`).
          ({ type: 'market' } as const)
        : jobId
          ? ({ type: 'job', id: Number(jobId) } as const)
          : offerId
            ? ({ type: 'job', id: Number(offerId) } as const)
            : employeeId
              ? ({ type: 'employee', id: Number(employeeId) } as const)
              : teamId
                ? ({ type: 'team', id: Number(teamId), evaluation: 'owns' } as const)
                : groupId
                  ? ({ type: 'org_group', id: groupId } as const)
                  : precedence === 'params'
                    ? search.first
                    : undefined)

  const second =
    (precedence === 'search' ? search.second : undefined) ??
    (applicantId
      ? ({ type: 'applicant', id: Number(applicantId) } as const)
      : precedence === 'params'
        ? search.second
        : undefined)

  return { first, second }
}

// One home for every report-scoped key: spelled out by hand at ~40 links, the shared ones were missed and
// leaked a stale collective onto an unrelated profile.
export function clearProfileSelection<T extends object>(prev: T) {
  return {
    ...prev,
    first: undefined,
    second: undefined,
    selected: undefined,
    collective: undefined,
    collectiveFilters: undefined,
  }
}

/** Entering another section opens a new conversation, which shows that section's overview, in place of the last one. */
export const FRESH_CONVERSATION = { chat: undefined, chatHistory: undefined }

// `collective` deliberately survives into compare mode so "Back to list" can return, hence the `selected` check.
export function isCollectiveActive(search: { collective?: string; selected: string }) {
  return search.collective != null && search.selected !== 'compare'
}

/**
 * The report has been retargeted away from the record its URL names: a pinned collective ranks a whole list
 * against it, compare mode puts a second profile beside it. A list marking its selected row from a ROUTE param
 * has to ask — the lists keyed on `second` are emptied by the same writes on their own.
 */
export function isProfileSelectionRetargeted(search: { collective?: string; selected: string }) {
  return search.collective != null || search.selected === 'compare'
}

export function useRootSearch(opts: { precedence: 'search' | 'params' } = { precedence: 'search' }) {
  const search = useSearch({ from: '__root__' })
  const params: ProfileRouteParams = useParams({ strict: false })
  const isReportPage = Boolean(useMatch({ from: '/_app/report', shouldThrow: false }))
  const isMarketPage = Boolean(useMatch({ from: '/_app/market', shouldThrow: false }))
  const precedence = opts.precedence

  return { ...search, ...deriveProfiles(search, params, isReportPage, precedence, isMarketPage) }
}
